import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import type { ApiError } from '@/core/api';
import { useChangePassword } from '../queries';

const schema = z
  .object({
    old_password: z.string().min(1, 'Enter your current password'),
    new_password1: z.string().min(8, 'Use at least 8 characters'),
    new_password2: z.string().min(1, 'Confirm your new password'),
  })
  .refine((v) => v.new_password1 === v.new_password2, {
    path: ['new_password2'],
    message: 'Passwords do not match',
  });

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = ['old_password', 'new_password1', 'new_password2'] as const;

export function ChangePasswordForm() {
  const change = useChangePassword();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { old_password: '', new_password1: '', new_password2: '' },
  });

  async function onSubmit(values: FormValues) {
    try {
      await change.mutateAsync(values);
      form.reset();
    } catch (e) {
      const fe = (e as ApiError)?.fieldErrors;
      if (fe) {
        for (const [field, messages] of Object.entries(fe)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      } else {
        form.setError('root', { message: (e as ApiError)?.message ?? 'Could not change password.' });
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-sm space-y-4">
        <FormField
          control={form.control}
          name="old_password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current password</FormLabel>
              <FormControl>
                <Input type="password" autoComplete="current-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="new_password1"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New password</FormLabel>
              <FormControl>
                <Input type="password" autoComplete="new-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="new_password2"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm new password</FormLabel>
              <FormControl>
                <Input type="password" autoComplete="new-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </Form>
  );
}
